import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DailyReportResponseV4Schema } from "../response/DailyReportResponseV4";

const API_VERSION = 'v4';

// Tipe spesifik untuk kolom yang bisa diurutkan
type OrderByOption = "date" | "rooms" | "room_sold" | "daily_revenue" | "revpar" | "occupancy" | "available";

// Interface for filter parameters
interface FilterPropsV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "stay_date" | "created_reservation";
    room_type_id?: string;
    ota_code?: string;
    agent_id?: string;
}

// Interface for the hook's parameters
interface DailyReportV4Props {
    filter: FilterPropsV4;
    orderBy?: OrderByOption; // Menggunakan tipe yang sudah didefinisikan
    sort?: "asc" | "desc";
    type: string | null;
}

/**
 * React Query hook to fetch the v4 daily report.
 * This version uses a POST request and handles the new API parameters.
 *
 * @param params - Filters, sorting options, and report type.
 * @returns The result of the useQuery hook.
 */
const useDailyReportV4 = (params: DailyReportV4Props) => {
    return useQuery({
        queryKey: [
            'report',
            'daily-report-v4',
            params.type,
            JSON.stringify(params.filter),
            params.orderBy,
            params.sort,
        ],
        queryFn: async () => {
            try {
                // Construct the request body
                const requestBody = {
                    filter: params.filter,
                    orderBy: params.orderBy || "date", // default orderBy
                    sort: params.sort || "asc",       // default sort
                };

                const response = await privateApi.get(`/${API_VERSION}/channel-manager/property-report-daily`, {
                    params: requestBody
                });

                // Validate the response with the Zod schema
                const validationResult = DailyReportResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid V4 Daily report data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch V4 Daily report`, error);
                throw error;
            }
        },
        // Enable the query only when the correct report type and required filters are present
        enabled: !!params.filter && !!params.filter.property_id && !!params.filter.start_date && !!params.filter.end_date && params.type === "daily_report",
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useDailyReportV4;